import React, { useState, useEffect } from 'react';
import { Modal, Input, Select, Spin, Upload, message, Switch } from 'antd';
import { ThemeButton } from '../components';
import { uploadImage } from '../../helpers/helpers';
import { useUpdateUserMutation } from '../../services/users';
import { US_STATES } from '../../constants/constants';
import { user as defaultAvatar } from '../../assets/images/images';

const { Option } = Select;

const EditUserModal = ({ isOpen, onClose, data, onSuccess, zIndex }) => {
  const [formData, setFormData] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [updateUser, { isLoading: updating }] = useUpdateUserMutation();

  // Initialize form data when modal opens or data changes
  useEffect(() => {
    if (data && isOpen) {
      setFormData({
        full_name: data.full_name || '',
        contact: data.contact || '',
        address: {
          street: data.address?.street || '',
          city: data.address?.city || '',
          state: data.address?.state || '',
          zipCode: data.address?.zipCode || '',
        },
        secondary_contact: {
          name: data.secondary_contact?.name || '',
          contact: data.secondary_contact?.contact || '',
        },
        avatar: data.avatar || '',
        active: data.active !== false, // Default to true if undefined
      });
    }
  }, [data, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleNestedChange = (parent, field, value) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }));
  };

  // Avatar upload
  const handleAvatarUpload = async file => {
    setAvatarUploading(true);
    try {
      const url = await uploadImage(file, file.name);
      if (url) {
        setFormData(prev => ({
          ...prev,
          avatar: url,
        }));
        message.success('Avatar uploaded successfully');
      } else {
        message.error('Failed to upload avatar');
      }
    } catch (error) {
      message.error('Error uploading avatar');
    }
    setAvatarUploading(false);
    return false;
  };

  // Submit form
  const handleSubmit = async () => {
    try {
      const result = await updateUser({
        id: data._id,
        body: formData,
      }).unwrap();

      if (result.error) {
        message.error(result.msg || 'Failed to update user');
      } else {
        message.success('User updated successfully');
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      message.error('Error updating user');
      console.error('Update error:', error);
    }
  };

  if (!formData) return null;

  return (
    <Modal
      width={600}
      title={
        <div className="text-xl font-semibold">
          Edit User: {data?.full_name || 'Unknown'}
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      zIndex={zIndex}
      footer={
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <ThemeButton
            content={updating ? <Spin size="small" /> : 'Save Changes'}
            onClick={handleSubmit}
            className="!h-10"
          />
        </div>
      }
      style={{ top: 20 }}
    >
      <div className="space-y-6 mt-4">
        {/* Account Status */}
        <div
          className={`p-4 rounded-lg border ${formData.active ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} ${data?.isSuperAdmin ? 'opacity-75' : ''}`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-700">
                Account Status
                {data?.isSuperAdmin && (
                  <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded bg-purple-100 text-purple-700 border border-purple-300">
                    Admin
                  </span>
                )}
              </p>
              <p
                className={`text-sm ${formData.active ? 'text-green-600' : 'text-red-600'}`}
              >
                {data?.isSuperAdmin
                  ? 'Admin accounts cannot be disabled'
                  : formData.active
                    ? 'Active - User can access the app'
                    : 'Disabled - User cannot access the app'}
              </p>
            </div>
            <Switch
              checked={formData.active}
              onChange={checked => handleInputChange('active', checked)}
              checkedChildren="Active"
              unCheckedChildren="Disabled"
              disabled={data?.isSuperAdmin}
            />
          </div>
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-4">
          <div className="relative">
            <img
              src={formData.avatar || defaultAvatar}
              alt="User avatar"
              className="w-20 h-20 rounded-full object-cover"
            />
          </div>
          <Upload
            beforeUpload={handleAvatarUpload}
            showUploadList={false}
            accept="image/*"
          >
            <ThemeButton
              content={avatarUploading ? <Spin size="small" /> : 'Change Photo'}
              className="!h-8"
            />
          </Upload>
        </div>

        {/* Read-only fields */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Email (read-only)
            </label>
            <Input value={data?.email || ''} disabled className="bg-gray-50" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Username (read-only)
            </label>
            <Input
              value={data?.username || ''}
              disabled
              className="bg-gray-50"
            />
          </div>
        </div>

        {/* Full Name & Contact */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Full Name
            </label>
            <Input
              value={formData.full_name}
              onChange={e => handleInputChange('full_name', e.target.value)}
              placeholder="Enter full name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Contact Number
            </label>
            <Input
              value={formData.contact}
              onChange={e => handleInputChange('contact', e.target.value)}
              placeholder="Enter contact number"
            />
          </div>
        </div>

        {/* Address */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Address</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Street
              </label>
              <Input
                value={formData.address.street}
                onChange={e =>
                  handleNestedChange('address', 'street', e.target.value)
                }
                placeholder="Enter street address"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  City
                </label>
                <Input
                  value={formData.address.city}
                  onChange={e =>
                    handleNestedChange('address', 'city', e.target.value)
                  }
                  placeholder="City"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  State
                </label>
                <Select
                  value={formData.address.state}
                  onChange={value =>
                    handleNestedChange('address', 'state', value)
                  }
                  className="w-full"
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="children"
                >
                  {US_STATES.map(state => (
                    <Option key={state.abbreviation} value={state.abbreviation}>
                      {state.abbreviation} - {state.name}
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  Zip Code
                </label>
                <Input
                  value={formData.address.zipCode}
                  onChange={e =>
                    handleNestedChange('address', 'zipCode', e.target.value)
                  }
                  placeholder="Zip code"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Contact */}
        <div>
          <h3 className="text-lg font-semibold mb-3">Secondary Contact</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Name
              </label>
              <Input
                value={formData.secondary_contact.name}
                onChange={e =>
                  handleNestedChange(
                    'secondary_contact',
                    'name',
                    e.target.value
                  )
                }
                placeholder="Contact name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Phone Number
              </label>
              <Input
                value={formData.secondary_contact.contact}
                onChange={e =>
                  handleNestedChange(
                    'secondary_contact',
                    'contact',
                    e.target.value
                  )
                }
                placeholder="Phone number"
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default EditUserModal;
